(function () {
"use strict";

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function isNearWhite(r, g, b, a, tol) {
  if (a === 0) return true;
  return r >= 255 - tol && g >= 255 - tol && b >= 255 - tol;
}

function fixWhiteFringe(imgData, options) {
  const w = imgData.width;
  const h = imgData.height;
  const data = imgData.data;
  const iterations = clamp(Number(options?.iterations ?? 2), 0, 6);
  const tol = clamp(Number(options?.tolerance ?? 18), 0, 80);
  const idx = (x, y) => (y * w + x) * 4;

  for (let it = 0; it < iterations; it++) {
    const copy = new Uint8ClampedArray(data);
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const o = idx(x, y);
        const a = copy[o + 3];
        if (a === 0) continue;
        const r = copy[o], g = copy[o + 1], b = copy[o + 2];
        if (!isNearWhite(r, g, b, a, tol)) continue;
        const n =
          (copy[idx(x - 1, y) + 3] === 0) ||
          (copy[idx(x + 1, y) + 3] === 0) ||
          (copy[idx(x, y - 1) + 3] === 0) ||
          (copy[idx(x, y + 1) + 3] === 0) ||
          (copy[idx(x - 1, y - 1) + 3] === 0) ||
          (copy[idx(x + 1, y - 1) + 3] === 0) ||
          (copy[idx(x - 1, y + 1) + 3] === 0) ||
          (copy[idx(x + 1, y + 1) + 3] === 0);
        if (!n) continue;

        let sr = 0, sg = 0, sb = 0, c = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const oo = idx(x + dx, y + dy);
            const na = copy[oo + 3];
            if (na === 0) continue;
            const nr = copy[oo], ng = copy[oo + 1], nb = copy[oo + 2];
            if (isNearWhite(nr, ng, nb, na, tol)) continue;
            sr += nr; sg += ng; sb += nb; c++;
          }
        }
        if (!c) continue;
        data[o] = (sr / c) | 0;
        data[o + 1] = (sg / c) | 0;
        data[o + 2] = (sb / c) | 0;
      }
    }
  }
}

function bleedColorsIntoTransparent(imgData, options) {
  const w = imgData.width;
  const h = imgData.height;
  const data = imgData.data;
  const radius = clamp(Number(options?.radius ?? 2), 0, 10);
  if (radius === 0) return;
  const idx = (x, y) => (y * w + x) * 4;

  for (let pass = 0; pass < radius; pass++) {
    const copy = new Uint8ClampedArray(data);
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const o = idx(x, y);
        if (copy[o + 3] !== 0) continue;
        let sr = 0, sg = 0, sb = 0, c = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const oo = idx(x + dx, y + dy);
            const a = copy[oo + 3];
            if (a === 0) continue;
            sr += copy[oo];
            sg += copy[oo + 1];
            sb += copy[oo + 2];
            c++;
          }
        }
        if (!c) continue;
        data[o] = (sr / c) | 0;
        data[o + 1] = (sg / c) | 0;
        data[o + 2] = (sb / c) | 0;
      }
    }
  }
}

function dehaloEdgePixels(imgData, options) {
  const w = imgData.width;
  const h = imgData.height;
  const data = imgData.data;
  const tol = clamp(Number(options?.tolerance ?? 18), 0, 80);
  const alphaDrop = clamp(Number(options?.alphaDrop ?? 80), 0, 255);
  const iterations = clamp(Number(options?.iterations ?? 2), 0, 6);
  const lumaThresh = clamp(Number(options?.lumaThresh ?? 200), 0, 255);
  const keepMask = options?.keepMask || null;

  const idx = (x, y) => (y * w + x) * 4;
  const nearWhite = (r, g, b, a) => isNearWhite(r, g, b, a, tol);
  const luma = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

  function hasTransparentNeighbor(copy, x, y) {
    return (
      copy[idx(x - 1, y) + 3] === 0 ||
      copy[idx(x + 1, y) + 3] === 0 ||
      copy[idx(x, y - 1) + 3] === 0 ||
      copy[idx(x, y + 1) + 3] === 0 ||
      copy[idx(x - 1, y - 1) + 3] === 0 ||
      copy[idx(x + 1, y - 1) + 3] === 0 ||
      copy[idx(x - 1, y + 1) + 3] === 0 ||
      copy[idx(x + 1, y + 1) + 3] === 0
    );
  }

  for (let it = 0; it < iterations; it++) {
    const copy = new Uint8ClampedArray(data);
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const o = idx(x, y);
        const mi = y * w + x;
        if (keepMask && keepMask[mi]) continue;
        const a = copy[o + 3];
        if (a === 0) continue;
        if (!hasTransparentNeighbor(copy, x, y)) continue;

        const r = copy[o], g = copy[o + 1], b = copy[o + 2];
        const isHalo = nearWhite(r, g, b, a) || luma(r, g, b) >= lumaThresh;
        if (!isHalo) continue;

        if (a <= alphaDrop) {
          data[o + 3] = 0;
          continue;
        }

        let sr = 0, sg = 0, sb = 0, c = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const oo = idx(x + dx, y + dy);
            const na = copy[oo + 3];
            if (na === 0) continue;
            const nr = copy[oo], ng = copy[oo + 1], nb = copy[oo + 2];
            if (nearWhite(nr, ng, nb, na)) continue;
            sr += nr; sg += ng; sb += nb; c++;
          }
        }
        if (!c) continue;
        data[o] = (sr / c) | 0;
        data[o + 1] = (sg / c) | 0;
        data[o + 2] = (sb / c) | 0;
      }
    }
  }
}

function tightenEdgeAlpha(imgData, options) {
  const w = imgData.width;
  const h = imgData.height;
  const data = imgData.data;
  const iterations = clamp(Number(options?.iterations ?? 1), 0, 6);
  const alphaCut = clamp(Number(options?.alphaCut ?? 200), 0, 255);
  const lumaThresh = clamp(Number(options?.lumaThresh ?? 170), 0, 255);
  const keepMask = options?.keepMask || null;

  const idx = (x, y) => (y * w + x) * 4;
  const luma = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

  function hasTransparentNeighbor(copy, x, y) {
    return (
      copy[idx(x - 1, y) + 3] === 0 ||
      copy[idx(x + 1, y) + 3] === 0 ||
      copy[idx(x, y - 1) + 3] === 0 ||
      copy[idx(x, y + 1) + 3] === 0 ||
      copy[idx(x - 1, y - 1) + 3] === 0 ||
      copy[idx(x + 1, y - 1) + 3] === 0 ||
      copy[idx(x - 1, y + 1) + 3] === 0 ||
      copy[idx(x + 1, y + 1) + 3] === 0
    );
  }

  for (let it = 0; it < iterations; it++) {
    const copy = new Uint8ClampedArray(data);
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const mi = y * w + x;
        if (keepMask && keepMask[mi]) continue;
        const o = idx(x, y);
        const a = copy[o + 3];
        if (a === 0) continue;
        if (!hasTransparentNeighbor(copy, x, y)) continue;
        const r = copy[o], g = copy[o + 1], b = copy[o + 2];
        if (luma(r, g, b) < lumaThresh) continue;
        if (a <= alphaCut) data[o + 3] = 0;
      }
    }
  }
}

function trimTransparentCanvas(srcCanvas, padding = 1) {
  const w = srcCanvas.width;
  const h = srcCanvas.height;
  const sctx = srcCanvas.getContext("2d", { willReadFrequently: true });
  if (!sctx) return srcCanvas;
  const img = sctx.getImageData(0, 0, w, h);
  const data = img.data;

  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const a = data[(y * w + x) * 4 + 3];
      if (a === 0) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX === -1) {
    const out = document.createElement("canvas");
    out.width = 1;
    out.height = 1;
    return out;
  }
  minX = clamp(minX - padding, 0, w - 1);
  minY = clamp(minY - padding, 0, h - 1);
  maxX = clamp(maxX + padding, 0, w - 1);
  maxY = clamp(maxY + padding, 0, h - 1);
  const outW = Math.max(1, maxX - minX + 1);
  const outH = Math.max(1, maxY - minY + 1);
  const out = document.createElement("canvas");
  out.width = outW;
  out.height = outH;
  const octx = out.getContext("2d");
  if (!octx) return srcCanvas;
  octx.drawImage(srcCanvas, minX, minY, outW, outH, 0, 0, outW, outH);
  return out;
}

function parseEmbroideryDigitFromFilename(name) {
  const raw = String(name || "").trim();
  if (!raw) return null;
  const noHash = raw.split("#")[0];
  const noQuery = noHash.split("?")[0];
  const tail = noQuery.split("/").pop() || noQuery;
  let normalized = tail;
  try {
    normalized = decodeURIComponent(tail);
  } catch (e) {}
  const m = normalized.match(/(\d{3})(?=\.[^.]+$)/);
  if (!m) return null;
  const code = m[1];
  return code?.[1] ?? null;
}

function parseEmbroideryDigitFromSku(identifier) {
  const raw = String(identifier || "").trim();
  if (!raw) return null;
  const parts = raw
    .split("|")
    .map((x) => String(x || "").trim())
    .filter(Boolean);
  const candidates = parts.length ? parts : [raw];
  for (const part of candidates) {
    const m = part.match(/(\d{2,}-\d{3}-(\d{3}))/);
    if (!m) continue;
    const lastTriplet = m[2] || "";
    if (lastTriplet.length !== 3) continue;
    const digit = lastTriplet[1];
    if (digit) return digit;
  }
  return null;
}

function detectLikelyWhiteEmbroidery(srcBitmap) {
  const w = Number(srcBitmap?.width || 0);
  const h = Number(srcBitmap?.height || 0);
  if (w < 10 || h < 10) return false;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  if (!ctx) return false;
  ctx.drawImage(srcBitmap, 0, 0, w, h);
  const imgData = ctx.getImageData(0, 0, w, h);
  const data = imgData.data;
  const idx = (x, y) => (y * w + x) * 4;
  const luma = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

  let whiteDetailHits = 0;
  const innerBand = Math.max(2, Math.floor(Math.min(w, h) * 0.03));
  for (let y = innerBand; y < h - innerBand; y += 2) {
    for (let x = innerBand; x < w - innerBand; x += 2) {
      const o = idx(x, y);
      const a = data[o + 3];
      if (a < 130) continue;
      const r = data[o], g = data[o + 1], b = data[o + 2];
      if (!isNearWhite(r, g, b, a, 26)) continue;
      let darkNeighbors = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const no = idx(x + dx, y + dy);
          const na = data[no + 3];
          if (na < 120) continue;
          const nr = data[no], ng = data[no + 1], nb = data[no + 2];
          if (luma(nr, ng, nb) <= 145) darkNeighbors++;
        }
      }
      if (darkNeighbors >= 3) whiteDetailHits++;
      if (whiteDetailHits >= 55) return true;
    }
  }
  return false;
}

function applyEdgeStroke(canvasIn, options) {
  const color = options?.color || { r: 0, g: 0, b: 0 };
  const thickness = clamp(Number(options?.thickness ?? 1), 1, 4);
  const outer = Boolean(options?.outer ?? false);

  const w = canvasIn.width;
  const h = canvasIn.height;
  const ctx = canvasIn.getContext("2d", { willReadFrequently: true });
  if (!ctx) return canvasIn;
  const img = ctx.getImageData(0, 0, w, h);
  const data = img.data;
  const copy = new Uint8ClampedArray(data);

  const idx = (x, y) => (y * w + x) * 4;
  const alphaAt = (x, y) => copy[idx(x, y) + 3];

  for (let y = thickness; y < h - thickness; y++) {
    for (let x = thickness; x < w - thickness; x++) {
      const o = idx(x, y);
      const a = copy[o + 3];
      if (!outer && a === 0) continue;
      if (outer && a !== 0) continue;
      let nearTransparent = false;
      for (let dy = -thickness; dy <= thickness && !nearTransparent; dy++) {
        for (let dx = -thickness; dx <= thickness; dx++) {
          if (dx === 0 && dy === 0) continue;
          const na = alphaAt(x + dx, y + dy);
          if ((!outer && na === 0) || (outer && na !== 0)) {
            nearTransparent = true;
            break;
          }
        }
      }
      if (!nearTransparent) continue;
      data[o] = color.r;
      data[o + 1] = color.g;
      data[o + 2] = color.b;
      data[o + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvasIn;
}

function cleanupOuterBandNoWhite(canvasIn, options) {
  const band = clamp(Number(options?.band ?? 6), 1, 20);
  const lumaThresh = clamp(Number(options?.lumaThresh ?? 155), 0, 255);
  const alphaCut = clamp(Number(options?.alphaCut ?? 245), 0, 255);
  const preferDarkLuma = clamp(Number(options?.preferDarkLuma ?? 120), 0, 255);

  const w = canvasIn.width;
  const h = canvasIn.height;
  const ctx = canvasIn.getContext("2d", { willReadFrequently: true });
  if (!ctx) return canvasIn;
  const img = ctx.getImageData(0, 0, w, h);
  const data = img.data;
  const copy = new Uint8ClampedArray(data);

  const idx = (x, y) => (y * w + x) * 4;
  const luma = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

  function sampleDarkNeighbor(x, y) {
    let sr = 0, sg = 0, sb = 0, c = 0;
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        if (dx === 0 && dy === 0) continue;
        const xx = x + dx, yy = y + dy;
        if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
        const o = idx(xx, yy);
        const a = copy[o + 3];
        if (a === 0) continue;
        const r = copy[o], g = copy[o + 1], b = copy[o + 2];
        if (luma(r, g, b) > preferDarkLuma) continue;
        sr += r; sg += g; sb += b; c++;
      }
    }
    if (!c) return null;
    return { r: (sr / c) | 0, g: (sg / c) | 0, b: (sb / c) | 0 };
  }

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const inBand = x < band || y < band || x >= w - band || y >= h - band;
      if (!inBand) continue;
      const o = idx(x, y);
      const a = copy[o + 3];
      if (a === 0) continue;
      const r = copy[o], g = copy[o + 1], b = copy[o + 2];
      if (luma(r, g, b) < lumaThresh) continue;

      if (a <= alphaCut) {
        data[o + 3] = 0;
        continue;
      }
      const dn = sampleDarkNeighbor(x, y);
      if (dn) {
        data[o] = dn.r;
        data[o + 1] = dn.g;
        data[o + 2] = dn.b;
        data[o + 3] = 255;
      } else {
        data[o] = 0; data[o + 1] = 0; data[o + 2] = 0; data[o + 3] = 255;
      }
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvasIn;
}

function suppressLightEdgeHalo(canvasIn, options) {
  const passes = clamp(Number(options?.passes ?? 2), 0, 6);
  const lumaThresh = clamp(Number(options?.lumaThresh ?? 178), 0, 255);
  const alphaMax = clamp(Number(options?.alphaMax ?? 250), 0, 255);
  const darkNeighborLuma = clamp(Number(options?.darkNeighborLuma ?? 165), 0, 255);
  const minDarkNeighbors = clamp(Number(options?.minDarkNeighbors ?? 2), 0, 8);
  if (passes <= 0) return canvasIn;

  const w = canvasIn.width;
  const h = canvasIn.height;
  const ctx = canvasIn.getContext("2d", { willReadFrequently: true });
  if (!ctx) return canvasIn;
  const img = ctx.getImageData(0, 0, w, h);
  const data = img.data;
  const idx = (x, y) => (y * w + x) * 4;
  const luma = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

  function hasTransparentNeighbor(copy, x, y) {
    return (
      copy[idx(x - 1, y) + 3] === 0 ||
      copy[idx(x + 1, y) + 3] === 0 ||
      copy[idx(x, y - 1) + 3] === 0 ||
      copy[idx(x, y + 1) + 3] === 0 ||
      copy[idx(x - 1, y - 1) + 3] === 0 ||
      copy[idx(x + 1, y - 1) + 3] === 0 ||
      copy[idx(x - 1, y + 1) + 3] === 0 ||
      copy[idx(x + 1, y + 1) + 3] === 0
    );
  }

  for (let pass = 0; pass < passes; pass++) {
    const copy = new Uint8ClampedArray(data);
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const o = idx(x, y);
        const a = copy[o + 3];
        if (a === 0) continue;
        if (!hasTransparentNeighbor(copy, x, y)) continue;

        const r = copy[o], g = copy[o + 1], b = copy[o + 2];
        if (luma(r, g, b) < lumaThresh) continue;

        if (a <= alphaMax) {
          data[o + 3] = 0;
          continue;
        }

        let sr = 0, sg = 0, sb = 0, c = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const oo = idx(x + dx, y + dy);
            const na = copy[oo + 3];
            if (na < 180) continue;
            const nr = copy[oo], ng = copy[oo + 1], nb = copy[oo + 2];
            if (luma(nr, ng, nb) > darkNeighborLuma) continue;
            sr += nr;
            sg += ng;
            sb += nb;
            c++;
          }
        }
        if (c >= minDarkNeighbors) {
          data[o] = (sr / c) | 0;
          data[o + 1] = (sg / c) | 0;
          data[o + 2] = (sb / c) | 0;
        }
      }
    }
  }

  ctx.putImageData(img, 0, 0);
  return canvasIn;
}

function removeEdgeConnectedWhiteBackground(srcBitmap, options) {
  const tol = clamp(Number(options?.tolerance ?? 22), 0, 80);
  const feather = clamp(Number(options?.feather ?? 1), 0, 6);
  const fringe = clamp(Number(options?.fringe ?? 2), 0, 6);
  const trim = Boolean(options?.trim ?? false);
  const trimPadding = clamp(Number(options?.trimPadding ?? 1), 0, 20);
  const neighborMin = clamp(Number(options?.neighborMin ?? 5), 0, 8);
  const keepRadius = clamp(Number(options?.keepRadius ?? 2), 0, 6);
  const fgTol = clamp(Number(options?.fgTol ?? 10), 0, 40);
  const bleed = clamp(Number(options?.bleed ?? 2), 0, 10);
  const dehalo = clamp(Number(options?.dehalo ?? 2), 0, 6);
  const dehaloLuma = clamp(Number(options?.dehaloLuma ?? 200), 0, 255);
  const dehaloAlphaDrop = clamp(Number(options?.dehaloAlphaDrop ?? 90), 0, 255);
  const edgeTighten = clamp(Number(options?.edgeTighten ?? 0), 0, 6);
  const edgeAlphaCut = clamp(Number(options?.edgeAlphaCut ?? 200), 0, 255);
  const edgeLuma = clamp(Number(options?.edgeLuma ?? 170), 0, 255);
  const preserveWhiteDetails = Boolean(options?.preserveWhiteDetails);
  const detailInnerBand = clamp(Number(options?.detailInnerBand ?? 2), 0, 20);
  const detailDarkNeighborMin = clamp(Number(options?.detailDarkNeighborMin ?? 3), 0, 8);
  const detailDarkLumaMax = clamp(Number(options?.detailDarkLumaMax ?? 150), 0, 255);
  const detailExpand = clamp(Number(options?.detailExpand ?? 1), 0, 4);
  const detailAlphaMin = clamp(Number(options?.detailAlphaMin ?? 120), 0, 255);

  const w = srcBitmap.width;
  const h = srcBitmap.height;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const cctx = c.getContext("2d", { willReadFrequently: true });
  if (!cctx) return srcBitmap;
  cctx.clearRect(0, 0, w, h);
  cctx.drawImage(srcBitmap, 0, 0, w, h);

  const imgData = cctx.getImageData(0, 0, w, h);
  const data = imgData.data;
  const len = w * h;
  const mark = new Uint8Array(len);
  const visited = new Uint8Array(len);
  const keep = new Uint8Array(len);
  const q = new Int32Array(len);
  let qs = 0, qe = 0;

  function idx(x, y) { return y * w + x; }
  function nearWhiteAtIndex(i) {
    const o = i * 4;
    return isNearWhite(data[o], data[o + 1], data[o + 2], data[o + 3], tol);
  }
  function isStrongForeground(i) {
    const o = i * 4;
    const a = data[o + 3];
    if (a === 0) return false;
    return !isNearWhite(data[o], data[o + 1], data[o + 2], a, fgTol);
  }
  function nearWhiteAtXY(x, y) {
    if (x < 0 || y < 0 || x >= w || y >= h) return true;
    return nearWhiteAtIndex(idx(x, y));
  }
  function lumaAt(i) {
    const o = i * 4;
    return 0.2126 * data[o] + 0.7152 * data[o + 1] + 0.0722 * data[o + 2];
  }
  function neighborWhiteCount(x, y) {
    let c = 0;
    c += nearWhiteAtXY(x - 1, y) ? 1 : 0;
    c += nearWhiteAtXY(x + 1, y) ? 1 : 0;
    c += nearWhiteAtXY(x, y - 1) ? 1 : 0;
    c += nearWhiteAtXY(x, y + 1) ? 1 : 0;
    c += nearWhiteAtXY(x - 1, y - 1) ? 1 : 0;
    c += nearWhiteAtXY(x + 1, y - 1) ? 1 : 0;
    c += nearWhiteAtXY(x - 1, y + 1) ? 1 : 0;
    c += nearWhiteAtXY(x + 1, y + 1) ? 1 : 0;
    return c;
  }

  if (keepRadius > 0) {
    const tmp = new Uint8Array(len);
    for (let i = 0; i < len; i++) if (isStrongForeground(i)) tmp[i] = 1;
    keep.set(tmp);
    for (let k = 0; k < keepRadius; k++) {
      const next = new Uint8Array(keep);
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const i = idx(x, y);
          if (keep[i]) continue;
          const any =
            keep[i - 1] || keep[i + 1] || keep[i - w] || keep[i + w] ||
            keep[i - w - 1] || keep[i - w + 1] || keep[i + w - 1] || keep[i + w + 1];
          if (any) next[i] = 1;
        }
      }
      keep.set(next);
    }
  }

  if (preserveWhiteDetails) {
    const whiteDetailMask = new Uint8Array(len);
    for (let y = detailInnerBand; y < h - detailInnerBand; y++) {
      for (let x = detailInnerBand; x < w - detailInnerBand; x++) {
        const i = idx(x, y);
        if (!nearWhiteAtIndex(i)) continue;
        const o = i * 4;
        const a = data[o + 3];
        if (a < detailAlphaMin) continue;
        let darkNeighbors = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const ni = idx(x + dx, y + dy);
            const no = ni * 4;
            if (data[no + 3] < detailAlphaMin) continue;
            if (lumaAt(ni) <= detailDarkLumaMax) darkNeighbors++;
          }
        }
        if (darkNeighbors >= detailDarkNeighborMin) whiteDetailMask[i] = 1;
      }
    }

    if (detailExpand > 0) {
      for (let step = 0; step < detailExpand; step++) {
        const next = new Uint8Array(whiteDetailMask);
        for (let y = 1; y < h - 1; y++) {
          for (let x = 1; x < w - 1; x++) {
            const i = idx(x, y);
            if (whiteDetailMask[i]) continue;
            const any =
              whiteDetailMask[i - 1] || whiteDetailMask[i + 1] || whiteDetailMask[i - w] || whiteDetailMask[i + w] ||
              whiteDetailMask[i - w - 1] || whiteDetailMask[i - w + 1] || whiteDetailMask[i + w - 1] || whiteDetailMask[i + w + 1];
            if (any) next[i] = 1;
          }
        }
        whiteDetailMask.set(next);
      }
    }

    for (let i = 0; i < len; i++) {
      if (whiteDetailMask[i]) keep[i] = 1;
    }
  }

  function canRemove(i) {
    if (!nearWhiteAtIndex(i)) return false;
    if (keep[i]) return false;
    const x = i % w;
    const y = (i / w) | 0;
    return neighborWhiteCount(x, y) >= neighborMin;
  }

  function push(i) {
    if (visited[i]) return;
    visited[i] = 1;
    if (!canRemove(i)) return;
    q[qe++] = i;
    mark[i] = 1;
  }

  for (let x = 0; x < w; x++) {
    push(idx(x, 0));
    push(idx(x, h - 1));
  }
  for (let y = 0; y < h; y++) {
    push(idx(0, y));
    push(idx(w - 1, y));
  }

  while (qs < qe) {
    const i = q[qs++];
    const x = i % w;
    const y = (i / w) | 0;
    if (x > 0) push(i - 1);
    if (x < w - 1) push(i + 1);
    if (y > 0) push(i - w);
    if (y < h - 1) push(i + w);
  }

  if (feather > 0) {
    const tmp = new Uint8Array(len);
    tmp.set(mark);
    for (let k = 0; k < feather; k++) {
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const i = idx(x, y);
          if (tmp[i]) continue;
          const any =
            tmp[i - 1] || tmp[i + 1] || tmp[i - w] || tmp[i + w] ||
            tmp[i - w - 1] || tmp[i - w + 1] || tmp[i + w - 1] || tmp[i + w + 1];
          if (any && canRemove(i)) mark[i] = 1;
        }
      }
      tmp.set(mark);
    }
  }

  for (let i = 0; i < len; i++) if (mark[i]) data[i * 4 + 3] = 0;

  if (fringe > 0) fixWhiteFringe(imgData, { iterations: fringe, tolerance: Math.max(10, tol - 6) });
  if (dehalo > 0) {
    dehaloEdgePixels(imgData, {
      iterations: dehalo,
      tolerance: Math.max(10, tol - 8),
      alphaDrop: dehaloAlphaDrop,
      lumaThresh: dehaloLuma,
      keepMask: keep
    });
  }
  if (bleed > 0) bleedColorsIntoTransparent(imgData, { radius: bleed });
  if (edgeTighten > 0) tightenEdgeAlpha(imgData, { iterations: edgeTighten, alphaCut: edgeAlphaCut, lumaThresh: edgeLuma, keepMask: keep });

  cctx.putImageData(imgData, 0, 0);
  return trim ? trimTransparentCanvas(c, trimPadding) : c;
}

function processBenchCanvasFromBitmap(benchBitmap, filename) {
  const skuDigit = parseEmbroideryDigitFromSku(filename);
  const fileDigit = parseEmbroideryDigitFromFilename(filename);
  const d = skuDigit || fileDigit;
  const protectWhiteEmbroidery = d === "1" || (!d && detectLikelyWhiteEmbroidery(benchBitmap));
  const isBlackEmbroidery = d === "8";

  let outCanvas = removeEdgeConnectedWhiteBackground(benchBitmap, {
    tolerance: 26,
    feather: 2,
    fringe: 2,
    dehalo: protectWhiteEmbroidery ? 2 : 3,
    dehaloLuma: protectWhiteEmbroidery ? 212 : 185,
    dehaloAlphaDrop: protectWhiteEmbroidery ? 95 : 140,
    bleed: 4,
    edgeTighten: protectWhiteEmbroidery ? 1 : 2,
    edgeAlphaCut: protectWhiteEmbroidery ? 196 : 220,
    edgeLuma: protectWhiteEmbroidery ? 188 : 155,
    trim: true,
    trimPadding: 0,
    neighborMin: protectWhiteEmbroidery ? 5 : 0,
    keepRadius: protectWhiteEmbroidery ? 2 : 0,
    fgTol: protectWhiteEmbroidery ? 10 : 0,
    preserveWhiteDetails: true,
    detailInnerBand: protectWhiteEmbroidery ? 1 : 2,
    detailDarkNeighborMin: protectWhiteEmbroidery ? 2 : 3,
    detailDarkLumaMax: protectWhiteEmbroidery ? 175 : 150,
    detailExpand: protectWhiteEmbroidery ? 2 : 1,
    detailAlphaMin: protectWhiteEmbroidery ? 96 : 120
  });

  if (!protectWhiteEmbroidery) {
    outCanvas = cleanupOuterBandNoWhite(outCanvas, { band: 6, lumaThresh: 145, alphaCut: 255, preferDarkLuma: 120 });
  }

  if (isBlackEmbroidery) {
    outCanvas = suppressLightEdgeHalo(outCanvas, {
      passes: 3,
      lumaThresh: 158,
      alphaMax: 255,
      darkNeighborLuma: 182,
      minDarkNeighbors: 1
    });
  }

  if (d === "1") {
    outCanvas = applyEdgeStroke(outCanvas, { color: { r: 255, g: 255, b: 255 }, thickness: 1, outer: false });
  }

  return outCanvas;
}


function processBenchCanvasFromImage(img, filename) {
  return processBenchCanvasFromBitmap(img, filename || "");
}
window.BundleBenchProcess = { processBenchCanvasFromImage };
})();
